// Supplier Brochure, Price Source, and Quote Request APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  SupplierBrochure, SupplierBrochureDetail, BrochureProduct,
  PriceSource, SupplierQuoteRequest,
} from '../quote-api-reference-types'

export const supplierBrochureApi = {
  // =========================================================================
  // SUPPLIER BROCHURE APIs
  // =========================================================================

  async listSupplierBrochures(supplierId: string): Promise<SupplierBrochure[]> {
    const client = getApiClient()
    const res = await client.get<SupplierBrochure[]>(
      `/quotebuilder/suppliers/${supplierId}/brochures/`,
      withOrgConfig()
    )
    return res.data
  },

  async getSupplierBrochure(supplierId: string, brochureId: string): Promise<SupplierBrochureDetail> {
    const client = getApiClient()
    const res = await client.get<SupplierBrochureDetail>(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/`,
      withOrgConfig()
    )
    return res.data
  },

  async uploadSupplierBrochure(
    supplierId: string,
    file: File,
    options?: {
      title?: string
      description?: string
      effectiveDate?: string
      expiryDate?: string
      autoExtract?: boolean
    }
  ): Promise<SupplierBrochureDetail> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)
    if (options?.title) formData.append('title', options.title)
    if (options?.description) formData.append('description', options.description)
    if (options?.effectiveDate) formData.append('effective_date', options.effectiveDate)
    if (options?.expiryDate) formData.append('expiry_date', options.expiryDate)
    formData.append('auto_extract', String(options?.autoExtract !== false))

    const res = await client.post<SupplierBrochureDetail>(
      `/quotebuilder/suppliers/${supplierId}/brochures/upload/`,
      formData,
      withOrgConfig({
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    )
    return res.data
  },

  async triggerBrochureExtraction(supplierId: string, brochureId: string): Promise<SupplierBrochureDetail> {
    const client = getApiClient()
    const res = await client.post<SupplierBrochureDetail>(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/extract/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  async getBrochureVersions(supplierId: string, brochureId: string): Promise<SupplierBrochure[]> {
    const client = getApiClient()
    const res = await client.get<SupplierBrochure[]>(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/versions/`,
      withOrgConfig()
    )
    return res.data
  },

  async mapBrochureProduct(
    supplierId: string,
    brochureId: string,
    productId: string,
    masterProductId: string
  ): Promise<BrochureProduct> {
    const client = getApiClient()
    const res = await client.post<BrochureProduct>(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/products/${productId}/map_product/`,
      { product_id: masterProductId },
      withOrgConfig()
    )
    return res.data
  },

  async ignoreBrochureProduct(
    supplierId: string,
    brochureId: string,
    productId: string
  ): Promise<BrochureProduct> {
    const client = getApiClient()
    const res = await client.post<BrochureProduct>(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/products/${productId}/ignore/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  async createMasterFromBrochureProduct(
    supplierId: string,
    brochureId: string,
    productId: string
  ): Promise<{ brochure_product: BrochureProduct; master_product: { id: string; name: string; code: string } }> {
    const client = getApiClient()
    const res = await client.post(
      `/quotebuilder/suppliers/${supplierId}/brochures/${brochureId}/products/${productId}/create_master_product/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  // =========================================================================
  // PRICE SOURCE TRACEABILITY APIs
  // =========================================================================

  async getLineItemPriceSource(lineItemId: string): Promise<PriceSource | null> {
    try {
      const client = getApiClient()
      const res = await client.get<PriceSource[]>(
        `/quotebuilder/price-sources/`,
        withOrgConfig({ params: { line_item_id: lineItemId } })
      )
      return res.data?.[0] || null
    } catch {
      return null
    }
  },

  async listPriceSources(params?: { line_item_id?: string }): Promise<PriceSource[]> {
    const client = getApiClient()
    const res = await client.get<PriceSource[]>(
      `/quotebuilder/price-sources/`,
      withOrgConfig({ params })
    )
    return res.data
  },

  // =========================================================================
  // SUPPLIER QUOTE REQUEST APIs
  // =========================================================================

  async listQuoteRequests(params?: { supplier_id?: string; status?: string }): Promise<SupplierQuoteRequest[]> {
    const client = getApiClient()
    const res = await client.get<SupplierQuoteRequest[]>(
      `/quotebuilder/quote-requests/`,
      withOrgConfig({ params })
    )
    return res.data
  },

  async createQuoteRequest(data: {
    supplier: string
    subject: string
    message?: string
    product_descriptions?: string
    response_deadline?: string
    contact_name?: string
    contact_email?: string
    products?: string[]  // BrochureProduct IDs
  }): Promise<SupplierQuoteRequest> {
    const client = getApiClient()
    const res = await client.post<SupplierQuoteRequest>(
      `/quotebuilder/quote-requests/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async sendQuoteRequest(requestId: string): Promise<SupplierQuoteRequest> {
    const client = getApiClient()
    const res = await client.post<SupplierQuoteRequest>(
      `/quotebuilder/quote-requests/${requestId}/send/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  async receiveQuoteResponse(
    requestId: string,
    data: { brochure_id?: string; notes?: string }
  ): Promise<SupplierQuoteRequest> {
    const client = getApiClient()
    const res = await client.post<SupplierQuoteRequest>(
      `/quotebuilder/quote-requests/${requestId}/receive_response/`,
      data,
      withOrgConfig()
    )
    return res.data
  },
}
